import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import useSeo from "@/hooks/useSeo";
import { hotSearches, searchMovies, searchNews } from "@/utils/data";

type TabKey = "movies" | "news";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const tabParam = searchParams.get("tab");
  const tab: TabKey = tabParam === "news" ? "news" : "movies";

  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const movieResults = useMemo(() => searchMovies(query), [query]);
  const newsResults = useMemo(() => searchNews(query), [query]);

  useSeo({
    title: query
      ? `“${query}” 的搜索结果 - 百合影视`
      : "搜索 - 百合影视内容检索",
    description: query
      ? `在百合影视中搜索“${query}”，共找到 ${movieResults.length} 部影视作品与 ${newsResults.length} 条资讯。`
      : "在百合影视中搜索你感兴趣的百合题材电影、电视剧与资讯内容。",
    keywords: query
      ? `${query},百合影视搜索,${query}百合电影,${query}资讯`
      : "百合影视搜索,百合电影搜索,百合资讯搜索",
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    const value = input.trim();
    if (value) next.set("q", value);
    if (tab !== "movies") next.set("tab", tab);
    setSearchParams(next);
  };

  const switchTab = (next: TabKey) => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (next !== "movies") params.set("tab", next);
    setSearchParams(params);
  };

  const activeCount = tab === "movies" ? movieResults.length : newsResults.length;

  return (
    <div className="pt-24 md:pt-32">
      <div className="border-b border-background-200/60 bg-secondary-50">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "搜索" }]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            搜索
          </h1>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex h-12 flex-1 items-center gap-3 rounded-md border border-background-300/70 bg-background-50 px-4 transition-colors focus-within:border-primary-300">
              <span className="flex h-4 w-4 items-center justify-center text-base text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视名称、题材关键词，如：治愈、校园、重逢"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空"
                  onClick={() => setInput("")}
                  className="flex h-5 w-5 items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-search-line" />
              </span>
              搜索
            </button>
          </form>

          {query ? (
            <p className="mt-4 text-sm text-foreground-600">
              关键词：
              <strong className="text-foreground-950">「{query}」</strong>
              ，共找到{" "}
              <strong className="text-primary-600">
                {movieResults.length + newsResults.length}
              </strong>{" "}
              条结果
            </p>
          ) : (
            <p className="mt-4 text-sm text-foreground-600">
              输入关键词，搜索你感兴趣的百合影视作品与资讯内容。
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        {query ? (
          <>
            <div className="inline-flex items-center gap-1 rounded-full bg-secondary-100 p-1">
              <button
                type="button"
                onClick={() => switchTab("movies")}
                className={`whitespace-nowrap rounded-full px-6 py-2 text-sm transition-colors ${
                  tab === "movies"
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                影视作品（{movieResults.length}）
              </button>
              <button
                type="button"
                onClick={() => switchTab("news")}
                className={`whitespace-nowrap rounded-full px-6 py-2 text-sm transition-colors ${
                  tab === "news"
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                新闻资讯（{newsResults.length}）
              </button>
            </div>

            <div className="mt-8">
              {activeCount > 0 ? (
                tab === "movies" ? (
                  <div className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
                    {movieResults.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-4 lg:grid-cols-2">
                    {newsResults.map((item) => (
                      <NewsCard key={item.id} item={item} />
                    ))}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-50 px-6 py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
                    <i className="ri-search-eye-line" />
                  </span>
                  <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
                    没有找到相关{tab === "movies" ? "影视作品" : "资讯"}
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-foreground-600">
                    换个关键词试试，或者看看下面的热门搜索。
                  </p>
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                    {hotSearches.map((word) => (
                      <Link
                        key={word}
                        to={`/search?q=${encodeURIComponent(word)}`}
                        className="whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-secondary-200"
                      >
                        {word}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-8 md:p-12">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-accent-500">
                <i className="ri-fire-line" />
              </span>
              热门搜索
            </h2>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {hotSearches.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="whitespace-nowrap rounded-full border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
                >
                  {word}
                </Link>
              ))}
            </div>

            <h2 className="mt-10 font-heading text-lg font-semibold text-foreground-950">
              试试这样搜
            </h2>
            <ul className="mt-4 space-y-2 text-sm text-foreground-600">
              <li>· 按题材搜索：治愈、校园、都市、文艺、甜宠</li>
              <li>· 按作品名称搜索：雨后花园、追光少女、玻璃花房</li>
              <li>· 按地区搜索：中国大陆、日本、韩国、中国台湾</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}